import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveFooter, ResponsiveHeader } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import {
  CreditCardIcon,
  CarIcon,
  HomeIcon,
  MedicalIcon,
  TaxIcon,
  BankIcon,
  DocumentsIcon,
} from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface DebtCategory {
  key: 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';
  label: string;
  Icon: React.FC<{ className?: string }>;
  description: string;
}

interface DebtItem {
  id: string;
  category: DebtCategory['key'];
  creditor: string;
  amount?: number;
  accountNumber?: string;
  dueDate?: string;
  notes?: string;
  documents?: AppDocument[];
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame235: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();
  const [selectedCategory, setSelectedCategory] = useState<DebtCategory | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDebtId, setEditingDebtId] = useState<string | null>(null);

  const loadDebts = (): DebtItem[] => {
    try {
      const sessionDebts = session.taskProgress?.['debts']?.answers?.debts || [];
      if (sessionDebts.length > 0) {
        return sessionDebts.map((debt: DebtItem) => ({
          ...debt,
          documents: normalizeDocuments(debt.documents || []),
        }));
      }

      const backup = localStorage.getItem('debts-backup');
      if (backup) {
        const parsed = JSON.parse(backup);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((debt: DebtItem) => ({
            ...debt,
            documents: normalizeDocuments(debt.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame235] Error loading debts:', error);
    }
    return [];
  };

  const [debts, setDebts] = useState<DebtItem[]>(() => loadDebts());

  useEffect(() => {
    const sessionDebts = session.taskProgress?.['debts']?.answers?.debts;
    if (sessionDebts) {
      const normalized = sessionDebts.map((debt: DebtItem) => ({
        ...debt,
        documents: normalizeDocuments(debt.documents || []),
      }));

      if (JSON.stringify(normalized) !== JSON.stringify(debts)) {
        setDebts(normalized);
      }
    }
  }, [session.taskProgress?.['debts']?.answers?.debts]);

  const persistDebts = async (updatedDebts: DebtItem[]) => {
    try {
      localStorage.setItem('debts-backup', JSON.stringify(updatedDebts));
    } catch (error) {
      console.error('[Frame235] Failed to save debts backup:', error);
    }

    await updateTaskProgress('debts', { debts: updatedDebts }, 'in-progress');
    setDebts(updatedDebts);
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame235] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  // Form state
  const [creditor, setCreditor] = useState('');
  const [amount, setAmount] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  const categories: DebtCategory[] = [
    {
      key: 'credit_card',
      label: 'Credit Card\nDebt',
      Icon: CreditCardIcon,
      description: 'Credit cards, store cards, and other revolving credit accounts',
    },
    {
      key: 'personal_auto',
      label: 'Personal &\nAuto Loans',
      Icon: CarIcon,
      description: 'Personal loans, auto loans, and other installment loans',
    },
    {
      key: 'mortgage',
      label: 'Property\nMortgages',
      Icon: HomeIcon,
      description: 'Home mortgages, HELOCs, and other property-related loans',
    },
    {
      key: 'medical',
      label: 'Medical\nExpenses',
      Icon: MedicalIcon,
      description: 'Outstanding medical bills, payment plans, and anticipated expenses',
    },
    {
      key: 'tax',
      label: 'Tax\nDebt',
      Icon: TaxIcon,
      description: 'Federal, state, and local tax obligations',
    },
    {
      key: 'other',
      label: 'Other\nLoans',
      Icon: BankIcon,
      description: 'Business loans, student loans, and other financial obligations',
    },
  ];

  const resetForm = () => {
    setCreditor('');
    setAmount('');
    setAccountNumber('');
    setDueDate('');
    setNotes('');
    setDocuments([]);
    setEditingDebtId(null);
    setIsUploadingDocuments(false);
  };

  const handleCategoryClick = (category: DebtCategory) => {
    setSelectedCategory(category);
    resetForm();
    setIsModalOpen(true);
  };

  const handleFileUpload = async (files: File[]) => {
    if (!files.length) return;

    setIsUploadingDocuments(true);

    try {
      const uploadedDocs = await Promise.all(
        files.map((file) => documentService.upload(file))
      );

      setDocuments((prev) => [...prev, ...uploadedDocs]);
    } catch (error) {
      console.error('[Frame235] Failed to upload document(s):', error);
      alert('Failed to upload one or more documents.');
    } finally {
      setIsUploadingDocuments(false);
    }
  };

  const handleEditDebt = (debt: DebtItem) => {
    setEditingDebtId(debt.id);
    setSelectedCategory(categories.find((c) => c.key === debt.category) || null);
    setCreditor(debt.creditor);
    setAmount(typeof debt.amount === 'number' ? debt.amount.toString() : '');
    setAccountNumber(debt.accountNumber || '');
    setDueDate(debt.dueDate || '');
    setNotes(debt.notes || '');
    setDocuments(normalizeDocuments(debt.documents || []));
    setIsModalOpen(true);
  };

  const handleDeleteDebt = async (debtId: string) => {
    if (!confirm('Are you sure you want to delete this debt entry?')) {
      return;
    }

    const debtToDelete = debts.find((d) => d.id === debtId);
    const attachedDocs = debtToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame235] Failed to delete document while deleting debt:', error);
      }
    }

    const updatedDebts = debts.filter((d) => d.id !== debtId);
    await persistDebts(updatedDebts);
  };

  const handleRemoveAttachedDocument = async (debtId: string, documentIndex: number) => {
    const debt = debts.find((d) => d.id === debtId);
    const docToRemove = debt?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame235] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDebts = debts.map((debt) => {
      if (debt.id !== debtId) return debt;
      return {
        ...debt,
        documents: normalizeDocuments(debt.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    await persistDebts(updatedDebts);
  };

  const handleSave = async () => {
    if (!selectedCategory || !creditor) {
      alert('Please fill in all required fields');
      return;
    }

    const normalizedAmount = amount.trim() ? parseFloat(amount.replace(/,/g, '')) : undefined;

    const debtData: DebtItem = {
      id: editingDebtId || Date.now().toString(),
      category: selectedCategory.key,
      creditor,
      ...(normalizedAmount !== undefined ? { amount: normalizedAmount } : {}),
      accountNumber,
      dueDate,
      notes,
      documents,
    };

    const updatedDebts = editingDebtId
      ? debts.map((d) => (d.id === editingDebtId ? debtData : d))
      : [...debts, debtData];

    await persistDebts(updatedDebts);

    setIsModalOpen(false);
    resetForm();
  };

  const handleNext = async () => {
    try {
      await updateTaskProgress('debts', { debts: debts ?? [] }, 'in-progress');
    } catch (e) {
      console.error('[Frame235] Failed to save debts progress:', e);
    }

    navigate('/frame-237');
  };

  const handleBack = () => navigate('/frame-234');

  const renderDocuments = (docs: AppDocument[] = [], debtId: string) => {
    const normalizedDocs = normalizeDocuments(docs);
    if (!normalizedDocs || normalizedDocs.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        <div className="space-y-2">
          {normalizedDocs.map((doc, index) => (
            <div
              key={`${debtId}-doc-${doc.id || index}-${index}`}
              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
            >
              <button
                type="button"
                onClick={() => openDocument(doc)}
                className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
              >
                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                <span
                  className="text-sm font-medium text-stone-800 truncate"
                  title={doc.name}
                >
                  {doc.name}
                </span>
              </button>

              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <button
                  type="button"
                  onClick={() => downloadDocument(doc)}
                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                  title="Download document"
                >
                  Download
                </button>

                <button
                  type="button"
                  onClick={() => handleRemoveAttachedDocument(debtId, index)}
                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                  title="Remove document"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame235] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Track Your Debts and Liabilities" />
        <div className="max-w-4xl">
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] mb-12">
            Document your outstanding debts and financial obligations. This information will help
            your loved ones manage and settle these accounts when the time comes.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <button
                key={category.key}
                onClick={() => handleCategoryClick(category)}
                className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
              >
                <div className="flex items-center gap-4">
                  <category.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-stone-800 whitespace-pre-line leading-tight">
                      {category.label}
                    </h3>
                    <p className="text-sm text-stone-600 mt-1">{category.description}</p>
                    {debts.filter((d) => d.category === category.key).length > 0 && (
                      <div className="mt-2 text-cyan-600 text-sm font-medium">
                        {debts.filter((d) => d.category === category.key).length} items added
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {debts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-stone-800 text-xl font-semibold font-['Work_Sans'] mb-4">
                Added Debts and Liabilities
              </h2>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {debts.map((debt) => {
                  const category = categories.find((c) => c.key === debt.category);
                  const detailParts: string[] = [];
                  if (typeof debt.amount === 'number') {
                    detailParts.push(`Amount: $${debt.amount.toLocaleString()}`);
                  }
                  if (debt.dueDate) {
                    detailParts.push(`Due: ${new Date(debt.dueDate).toLocaleDateString()}`);
                  }
                  if (debt.accountNumber) {
                    detailParts.push(`Account ending in ${debt.accountNumber.slice(-4)}`);
                  }
                  return (
                    <div
                      key={debt.id}
                      className="bg-white rounded-lg border border-neutral-200 p-4"
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                          {category && <category.Icon className="w-6 h-6 text-cyan-700 flex-shrink-0" />}

                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-stone-800 break-words">{debt.creditor}</div>
                            {detailParts.length > 0 && (
                              <div className="text-sm text-stone-500 mt-1 break-words">
                                {detailParts.join(' • ')}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                          <button
                            onClick={() => handleEditDebt(debt)}
                            className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteDebt(debt.id)}
                            className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      {renderDocuments(debt.documents || [], debt.id)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ResponsiveContent>

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingDebtId ? 'Edit Debt Entry' : selectedCategory ? `Add ${selectedCategory.label.replace('\n', ' ')}` : ''}
        okText={editingDebtId ? 'Save Changes' : 'Add Debt'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => {
          setIsModalOpen(false);
          resetForm();
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Creditor Name*
            </label>
            <input
              type="text"
              value={creditor}
              onChange={(e) => setCreditor(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Estimated Amount (optional)
            </label>
            <input
              type="text"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Account Number (optional)
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Due Date (optional)
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-md"
              rows={3}
              placeholder="Add any additional notes"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents (optional)
            </label>
            <FileUpload onUpload={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" multiple />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents && documents.length > 0 && (
              <div className="mt-3 space-y-2">
                {documents.map((doc, idx) => (
                  <div
                    key={`${doc.id || idx}-${idx}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between text-sm bg-stone-50 p-2 border border-stone-200 rounded gap-3"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-600 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        type="button"
                        onClick={() => removeDocumentFromForm(idx)}
                        className="text-red-600 hover:text-red-800 text-xs font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ResponsiveFooter
        title="Track Debts and Final Expenses"
        subtitle="Document your debts and liabilities"
        progressPercent={33.33}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame235;